/**
 * How each kind of configurable content is described, named by **what the
 * editor gets** rather than by the manifest grammar's keyword.
 *
 * "richtext" and "variable" mean nothing to the person building a block;
 * "Formatted text" and "Value from the data" are the actual choice. The
 * description sits in the option rather than in a tooltip, because it is what
 * turns picking a type from a guess into a decision.
 */
import { F } from '@/utils/intl';
import React from 'react';
import { ParamKind } from '../../components/EmailBodyEditor/visual/manifest';

export const FIELD_TYPES: Record<
  ParamKind,
  { label: React.ReactNode; description: React.ReactNode }
> = {
  text: {
    label: <F id="pages.emailCenter.fields.kind.text" defaultMessage="Text" />,
    description: (
      <F
        id="pages.emailCenter.fields.kind.textHint"
        defaultMessage="A short line of plain text"
      />
    ),
  },
  richtext: {
    label: (
      <F
        id="pages.emailCenter.fields.kind.richtext"
        defaultMessage="Formatted text"
      />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.richtextHint"
        defaultMessage="Paragraphs with bold, links and lists"
      />
    ),
  },
  url: {
    label: <F id="pages.emailCenter.fields.kind.url" defaultMessage="Link" />,
    description: (
      <F
        id="pages.emailCenter.fields.kind.urlHint"
        defaultMessage="A web address to link to"
      />
    ),
  },
  image: {
    label: (
      <F id="pages.emailCenter.fields.kind.image" defaultMessage="Image" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.imageHint"
        defaultMessage="A picture, chosen or pasted as a link"
      />
    ),
  },
  color: {
    label: (
      <F id="pages.emailCenter.fields.kind.color" defaultMessage="Color" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.colorHint"
        defaultMessage="A color, or one of your brand colors"
      />
    ),
  },
  number: {
    label: (
      <F id="pages.emailCenter.fields.kind.number" defaultMessage="Number" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.numberHint"
        defaultMessage="A number, such as a width or a size"
      />
    ),
  },
  toggle: {
    label: (
      <F id="pages.emailCenter.fields.kind.toggle" defaultMessage="On / off" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.toggleHint"
        defaultMessage="A switch that shows or hides a part"
      />
    ),
  },
  select: {
    label: (
      <F id="pages.emailCenter.fields.kind.select" defaultMessage="Choice" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.selectHint"
        defaultMessage="One option out of a list you define"
      />
    ),
  },
  spacing: {
    label: (
      <F id="pages.emailCenter.fields.kind.spacing" defaultMessage="Spacing" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.spacingHint"
        defaultMessage="Padding on the four sides"
      />
    ),
  },
  variable: {
    label: (
      <F
        id="pages.emailCenter.fields.kind.variable"
        defaultMessage="Value from the data"
      />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.variableHint"
        defaultMessage="A lead, appointment or journey value"
      />
    ),
  },
  font: {
    label: <F id="pages.emailCenter.fields.kind.font" defaultMessage="Font" />,
    description: (
      <F
        id="pages.emailCenter.fields.kind.fontHint"
        defaultMessage="A font family, from the ones every mail client has"
      />
    ),
  },
  css: {
    label: (
      <F id="pages.emailCenter.fields.kind.css" defaultMessage="CSS styles" />
    ),
    description: (
      <F
        id="pages.emailCenter.fields.kind.cssHint"
        defaultMessage="Extra style rules, added to what the block already sets"
      />
    ),
  },
  code: {
    label: <F id="pages.emailCenter.fields.kind.code" defaultMessage="HTML" />,
    description: (
      <F
        id="pages.emailCenter.fields.kind.codeHint"
        defaultMessage="Raw HTML or Twig, edited as code"
      />
    ),
  },
};
